import axios from "axios";
import TurndownService from "turndown";
import Config from "../config";
import { tokenStorage } from "../utils/tokenStorage";
import PushNotificationService from "./PushNotificationService";

class ThreadWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.threadId = null;
    this.token = null;
    this.onMessageCallback = null;
    this.onOpenCallback = null;
    this.onCloseCallback = null;
    this.onErrorCallback = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.isConnecting = false;
    this.visibilityHandler = null;
    this.focusHandler = null;
    this.pendingMessage = null;
    this._setupVisibilityHandlers();
  }

  _setupVisibilityHandlers() {
    if (typeof document !== "undefined") {
      this.visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          if (this.token && (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN)) {
            console.log("WebSocket: Page visible, reconnecting...");
            this.reconnectAttempts = 0; // Reset attempts
            this.connect(this.token);
          }
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);

      this.focusHandler = () => {
        if (this.token && (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN)) {
          console.log("WebSocket: Window focused, reconnecting...");
          this.reconnectAttempts = 0; // Reset attempts
          this.connect(this.token);
        }
      };
      window.addEventListener("focus", this.focusHandler);
    }
  }

  _removeVisibilityHandlers() {
    if (typeof document !== "undefined" && this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (typeof window !== "undefined" && this.focusHandler) {
      window.removeEventListener("focus", this.focusHandler);
    }
  }

  async _resolveToken(passedToken) {
    if (passedToken) {
      return passedToken;
    }
    if (this.token) {
      return this.token;
    }

    const stored = await tokenStorage.getAccessToken();
    return stored || null;
  }

  async connect(token) {
    if (this.isConnecting) {
      return;
    }
    const resolvedToken = await this._resolveToken(token);
    if (!resolvedToken) {
      return;
    }

    this.token = resolvedToken;
    this.isConnecting = true;

    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      console.log(
        "WebSocket: Closing existing connection before creating new one",
      );
      this.ws.close();
      this.ws = null;
    }

    const baseWsUrl = process.env.REACT_APP_WEBSOCKET_URL || "wss://nachmed.ai/thread-socket";
    const wsUrl = `${baseWsUrl}?token=${encodeURIComponent(resolvedToken)}`;

    try {
      if (typeof window === "undefined" || !window.WebSocket) {
        this.isConnecting = false;
        return;
      }
      
      this.ws = new window.WebSocket(wsUrl);
      
      if (!this.ws) {
        console.error("WebSocket: Failed to create WebSocket instance");
        this.isConnecting = false;
        return;
      }

      this.ws.onopen = () => {
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        if (this.connectTimeout) {
          clearTimeout(this.connectTimeout);
          this.connectTimeout = null;
        }
        
        // Send any pending threadId now that connection is open
        if (this.pendingThreadId) {
          const threadIdToSend = this.pendingThreadId;
          this.threadId = threadIdToSend;
          // Use direct send since we know connection is open
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
              // Convert threadId to number if it's a string that represents a number
              const threadIdNum = typeof threadIdToSend === 'string' && !isNaN(threadIdToSend) ? parseInt(threadIdToSend, 10) : threadIdToSend;
              this.ws.send(JSON.stringify({ action: "set_thread", thread_id: threadIdNum }));
              this.pendingThreadId = null;
            } catch (error) {
              console.error("WebSocket: Error sending set_thread:", error);
            }
          }
        }
        
        // Send any pending message
        if (this.pendingMessage) {
          setTimeout(() => {
            if (this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN) {
              try {
                this.ws.send(JSON.stringify(this.pendingMessage));
                console.log("WebSocket: Sent pending message after reconnection");
                this.pendingMessage = null;
              } catch (error) {
                console.error("WebSocket: Error sending pending message:", error);
              }
            }
          }, 100);
        }
        
        // Call onOpen callback
        if (this.onOpenCallback) {
          console.log("WebSocket: Calling onOpen callback");
          this.onOpenCallback();
        } else {
          console.log("WebSocket: No onOpen callback registered");
        }
      };

      this.ws.onmessage = async (event) => {
        try {
          const messageData =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;

          const statusCode =
            messageData?.status ||
            messageData?.code ||
            messageData?.content?.status ||
            messageData?.content?.code;
          if (statusCode === 402) {
            await this._handleInvalidToken();
            return;
          }

          // Handle different message types
          if (messageData && messageData.type && messageData.content) {
            // For agent_status messages, process the content
            if (
              messageData.type === "agent_status" &&
              messageData.content.content
            ) {
              // Transform the message structure to match what ChatScreen expects
              const transformedMessage = {
                id: messageData.content.message_id,
                message: messageData.content.content,
                thread_id: messageData.content.thread_id,
                user_id: messageData.content.user_id,
                sender: messageData.content.sender,
                type: messageData.type,
                additional_data: messageData.content.additional_data,
                stance_analysis: messageData.content.stance_analysis || messageData.content.additional_data?.stance_analysis,
                intro_text: messageData.content.intro_text || messageData.content.additional_data?.intro_text,
              };

              // Call the callback with the transformed message
              if (this.onMessageCallback) {
                this.onMessageCallback(transformedMessage);
              }

              // Show push notification if page is hidden or not focused
              if (transformedMessage.sender !== "user") {
                const messagePreview = transformedMessage.message?.substring(0, 100) || "Нова відповідь";
                PushNotificationService.show("Nachmed AI", {
                  body: messagePreview + (transformedMessage.message?.length > 100 ? "..." : ""),
                  tag: `nachmed-${transformedMessage.thread_id}`,
                  onClick: () => {
                    window.focus();
                  },
                });
              }
            } else {
              // For other message types, pass through as-is but check for HTML content
              const htmlPattern = /<[^>]+>/;
              if (htmlPattern.test(messageData.content.content)) {
                const turndownService = new TurndownService();
                messageData.content.content = turndownService.turndown(
                  messageData.content.content,
                );

                const transformedSearchMessage = {
                  id: messageData.content?.message_id
                    ? messageData.content?.message_id
                    : messageData.content?.search_id,
                  message: messageData.content.content,
                  thread_id: messageData.content.thread_id,
                  user_id: messageData.content.user_id,
                  sender: messageData.content.sender,
                  type: messageData.type,
                  additional_data: messageData.content.additional_data,
                  stance_analysis: messageData.content.stance_analysis || messageData.content.additional_data?.stance_analysis,
                  intro_text: messageData.content.intro_text || messageData.content.additional_data?.intro_text,
                };

                if (this.onMessageCallback) {
                  this.onMessageCallback(transformedSearchMessage);
                }

                // Show push notification for search results
                if (transformedSearchMessage.sender !== "user") {
                  const messagePreview = transformedSearchMessage.message?.substring(0, 100) || "Нова відповідь";
                  PushNotificationService.show("Nachmed AI", {
                    body: messagePreview + (transformedSearchMessage.message?.length > 100 ? "..." : ""),
                    tag: `nachmed-${transformedSearchMessage.thread_id}`,
                    onClick: () => {
                      window.focus();
                    },
                  });
                }
              } else {
                const transformedMessage = {
                  id: messageData.content.message_id,
                  message: messageData.content.content,
                  thread_id: messageData.content.thread_id,
                  user_id: messageData.content.user_id,
                  sender: messageData.content.sender,
                  type: messageData.type,
                  additional_data: messageData.content.additional_data,
                  stance_analysis: messageData.content.stance_analysis || messageData.content.additional_data?.stance_analysis,
                  intro_text: messageData.content.intro_text || messageData.content.additional_data?.intro_text,
                };
                if (this.onMessageCallback) {
                  this.onMessageCallback(transformedMessage);
                }

                // Show push notification
                if (transformedMessage.sender !== "user") {
                  const messagePreview = transformedMessage.message?.substring(0, 100) || "Нова відповідь";
                  PushNotificationService.show("Nachmed AI", {
                    body: messagePreview + (transformedMessage.message?.length > 100 ? "..." : ""),
                    tag: `nachmed-${transformedMessage.thread_id}`,
                    onClick: () => {
                      window.focus();
                    },
                  });
                }
              }
            }
          } else {
            // Message doesn't match expected structure - try to pass through anyway
            if (this.onMessageCallback && messageData) {
              this.onMessageCallback(messageData);
            }
          }
        } catch (error) {
          console.error(
            "WebSocket: Error parsing or processing message",
            error,
          );
          // If parsing fails, still pass the raw data to the general callback
          if (this.onMessageCallback) {
            this.onMessageCallback(event.data);
          }
        }
      };

      this.ws.onclose = async (event) => {
        this.isConnected = false;
        this.isConnecting = false;
        if (this.onCloseCallback) this.onCloseCallback();

        if (event?.code === 402) {
          await this._handleInvalidToken();
          return;
        }

        // IMPROVED: Only reconnect if the close wasn't intentional
        if (event.code !== 1000 && event.code !== 1001) {
          await this._reconnectWithRefresh(event);
        }
      };

      this.ws.onerror = async (error) => {
        this.isConnected = false;
        this.isConnecting = false;
        if (this.onErrorCallback) this.onErrorCallback(error);
        
        // Try to refresh token and reconnect on error
        await this._reconnectWithRefresh();
      };

      // Set a timeout to detect if connection doesn't open within 10 seconds
      this.connectTimeout = setTimeout(async () => {
        if (!this.isConnected) {
          console.log("WebSocket connection timeout, attempting reconnect");
          this.isConnecting = false;
          await this._reconnectWithRefresh();
        }
      }, 10000); // 10 seconds
    } catch (error) {
      console.error("WebSocket: Error creating connection", error);
      this.isConnecting = false;
    }
  }

  async _reconnectWithRefresh(lastEvent) {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);

    // Stop reconnecting after a certain number of attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn("WebSocket: Max reconnection attempts reached.");
      return;
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.refreshInFlight) {
      console.log("WebSocket: Token refresh already in progress, waiting...");
      try {
        await this.refreshInFlight;
        // After refresh completes, it will reconnect automatically
        return;
      } catch (error) {
        console.error("WebSocket: Refresh failed during reconnect attempt", error);
        // Continue with normal reconnect using existing token
      }
    }

    // Try to refresh token first
    try {
      console.log("WebSocket: Attempting to refresh token before reconnect");
      await this._handleInvalidToken();
      // _handleInvalidToken will reconnect automatically after refresh
      return;
    } catch (error) {
      console.warn("WebSocket: Token refresh failed, attempting reconnect with existing token", error);
      // Fall through to normal reconnect
    }

    // Fallback to normal reconnect if refresh fails or no refresh token available
    await this._reconnect(lastEvent);
  }

  async _reconnect(lastEvent) {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);

    // Stop reconnecting after a certain number of attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn("WebSocket: Max reconnection attempts reached.");
      return;
    }

    // Don't reconnect if we don't have a token
    const token = await tokenStorage.getAccessToken();
    if (!token) {
      console.log("WebSocket: No token available, skipping reconnect");
      return;
    }

    this.token = token;
    this.reconnectAttempts++;
    // Exponential backoff with a cap (30 seconds)
    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts), 30000);
    console.log(
      `WebSocket: Attempting reconnect #${this.reconnectAttempts} in ${delay / 1000} seconds...`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect(this.token); // Reconnect using the stored token
    }, delay);
  }

  send(data) {
    // Normalize to async-friendly path while preserving the public API
    return this._sendInternal(data);
  }

  async _sendInternal(data) {
    // Check if WebSocket is ready
    if (this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        console.log("WebSocket: Message sent successfully");
        return true;
      } catch (error) {
        console.error("WebSocket: Error sending message:", error);
        return false;
      }
    }
    
    // If it's a set_thread action, always store the thread_id
    if (data.action === "set_thread" && data.thread_id) {
      this.pendingThreadId = data.thread_id;
      this.threadId = data.thread_id;
      console.log(`WebSocket: Stored thread_id ${data.thread_id} for later`);
      
      // If WebSocket is connecting, wait for it
      if (this.isConnecting) {
        console.log("WebSocket: Connection in progress, thread_id will be set after connection");
        return false;
      }
      
      // If not connected and we have (or can load) a token, try to connect
      const tokenForReconnect = await this._resolveToken();
      if (tokenForReconnect && !this.isConnecting) {
        console.log("WebSocket: Not connected, attempting to connect...");
        this.reconnectAttempts = 0;
        this.connect(tokenForReconnect);
        return false;
      }
      
      // If no token, can't connect
      if (!this.token) {
        console.warn("WebSocket: No token available, cannot connect");
        return false;
      }
    } else {
      // For other messages, store as pending
      if (data.thread_id) {
        this.pendingThreadId = data.thread_id;
      }
      this.pendingMessage = data;
      console.warn("WebSocket is not connected or not open. Message stored for later");
      
      // Try to reconnect if we have (or can load) a token
      const tokenForReconnect = await this._resolveToken();
      if (tokenForReconnect && !this.isConnecting) {
        console.log("WebSocket: Attempting to reconnect before sending message");
        this.reconnectAttempts = 0;
        this.connect(tokenForReconnect);
      } else if (!this.token) {
        console.warn("WebSocket: Cannot reconnect - no token available");
      }
    }
    
    return false;
  }

  close() {
    console.log("WebSocket: Explicitly closing connection");

    // Clear all timeouts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }

    // Reset state
    this.reconnectAttempts = 0;
    this.pendingThreadId = null;
    this.isConnecting = false;

    if (this.ws) {
      // Set a flag to prevent reconnection on this close
      this.ws.onclose = () => {
        this.isConnected = false;
        console.log("WebSocket: Explicitly closed by client.");
      };

      this.ws.close(1000, "Client disconnect");
      this.ws = null;
      this.isConnected = false;
    }

    // Remove visibility handlers when explicitly closing
    this._removeVisibilityHandlers();
  }

  onMessage(callback) {
    this.onMessageCallback = callback;
  }

  onOpen(callback) {
    this.onOpenCallback = callback;
  }

  onClose(callback) {
    this.onCloseCallback = callback;
  }

  onError(callback) {
    this.onErrorCallback = callback;
  }

  getStatus() {
    if (!this.ws) return "CLOSED";
    switch (this.ws.readyState) {
      case 0:
        return "CONNECTING";
      case 1:
        return "OPEN";
      case 2:
        return "CLOSING";
      case 3:
        return "CLOSED";
      default:
        return "UNKNOWN";
    }
  }

  // Helper method to check if WebSocket is ready
  isReady() {
    return this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN;
  }

  // Method to set thread and wait for connection if needed
  async setThread(threadId) {
    if (this.isReady()) {
      // If connected, send immediately
      try {
        // Convert threadId to number if it's a string that represents a number
        const threadIdNum = typeof threadId === 'string' && !isNaN(threadId) ? parseInt(threadId, 10) : threadId;
        const message = JSON.stringify({ action: "set_thread", thread_id: threadIdNum });
        
        // Double-check WebSocket state before sending
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          throw new Error("WebSocket not open");
        }
        
        this.ws.send(message);
        this.threadId = threadId;
        return true;
      } catch (error) {
        console.error("WebSocket: Error sending set_thread:", error);
        // Fall through to store as pending
      }
    }
    
    // Store for later (either not ready or send failed)
    this.pendingThreadId = threadId;
    this.threadId = threadId;
    
    // Try to connect if we have (or can load) a token and we're not already connected/connecting
    const tokenForReconnect = await this._resolveToken();
    if (tokenForReconnect && !this.isConnecting && !this.isReady()) {
      this.reconnectAttempts = 0;
      this.connect(tokenForReconnect);
    }
    return false;
  }

  async _handleInvalidToken() {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this._refreshTokens();
    try {
      await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  async _refreshTokens() {
    try {
      const refreshToken = await tokenStorage.getRefreshToken();
      if (!refreshToken) {
        console.warn("WebSocket: No refresh token available, closing connection");
        this.close();
        return;
      }

      const response = await axios.post(`${Config.baseUrl}auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { access_token: accessToken, refresh_token: newRefreshToken } =
        response.data || {};

      if (!accessToken) {
        console.warn("WebSocket: Refresh response missing access token");
        this.close();
        return;
      }

      await tokenStorage.setTokens(accessToken, newRefreshToken || refreshToken);
      this.token = accessToken;
      this.reconnectAttempts = 0;
      await this.connect(accessToken);
    } catch (error) {
      console.error("WebSocket: Failed to refresh token", error);
      this.close();
    }
  }
}

const webSocketService = new ThreadWebSocket();

export default webSocketService;
